import { isNone } from '@ember/utils';
import { deserializeFilter as deserializeAttribute } from 'cio/utils/conditions/deserialize/attribute';
import deserializeAttributeCompare from 'cio/utils/conditions/deserialize/attribute-compare';
import { any, all } from 'cio/app-models/conditions/group';
import { SerializedGroupConditionAnd, SerializedRelationshipCondition } from 'cio/types/conditions/condition';
import { SerializedLegacyAttributeFilter, SerializedLegacyAttributeFilters } from 'cio/types/conditions/legacy-filter';
import { isGroupAndCondition, isGroupOrCondition, isSimpleCondition } from 'cio/utils/conditions/deserialize';
import RelationshipCondition from 'cio/app-models/conditions/relationship';
import ObjectAttributeCondition, { ObjectAttributeSource } from 'cio/app-models/conditions/object-attribute';

type SerializedRelationshipAttributeFilter = { id: number } & {
  [key in ObjectAttributeSource]: SerializedLegacyAttributeFilter;
};

function filterToAttribute(filter: SerializedLegacyAttributeFilter, objectAttributeSource?: ObjectAttributeSource) {
  if (filter.type === 'attribute_compare') {
    return deserializeAttributeCompare({ ...filter });
  }

  return deserializeAttribute({
    ...filter,
    type: 'object_attribute',
    objectAttributeSource,
  }) as ObjectAttributeCondition;
}

export function deserializeFilters(
  tree: SerializedLegacyAttributeFilters | Record<string, never>,
): ObjectAttributeCondition[] {
  if (isNone(tree) || Object.keys(tree).length === 0) {
    return [];
  }

  if (isSimpleCondition(tree)) {
    return [filterToAttribute(tree) as ObjectAttributeCondition];
  }

  let group: SerializedLegacyAttributeFilters[] | null = null;
  if (isGroupAndCondition(tree)) {
    group = tree.and;
  } else if (isGroupOrCondition(tree)) {
    group = tree.or;
  }
  if (group) {
    let deserializedFilters = (group as unknown[]).map((f: SerializedRelationshipAttributeFilter) => {
      let filter = f[ObjectAttributeSource.ObjectAttribute] ?? f[ObjectAttributeSource.RelationshipAttribute] ?? f;
      let objectAttributeSource = Object.keys(f).find((k) => k !== 'id') as ObjectAttributeSource;

      return filterToAttribute(filter, objectAttributeSource) as ObjectAttributeCondition;
    });

    return deserializedFilters;
  }

  return [];
}

export function filterOperator(filters: SerializedLegacyAttributeFilters | Record<string, never> | null) {
  if (isNone(filters)) {
    return all;
  }

  if (isGroupOrCondition(filters)) {
    return any;
  } else {
    return all;
  }
}

export function isRelationshipGroupedCondition(json: any) {
  if (json?.and?.[0]?.relationship !== undefined) {
    return true;
  }
  return false;
}

// export default function (json: SerializedRelationshipCondition) {
export default function (json: SerializedGroupConditionAnd) {
  let [relationshipCondition, otherFilters] = json.and;
  let {
    relationship: { type_id, inverse },
  } = relationshipCondition as SerializedRelationshipCondition;
  return RelationshipCondition.create({
    typeId: String(type_id),
    inverse,
    filters: deserializeFilters(otherFilters as unknown as SerializedLegacyAttributeFilters),
    filterOperator: filterOperator(otherFilters as unknown as SerializedLegacyAttributeFilters),
  });
}
